import React, { useState, useEffect, useRef, ChangeEvent, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../../components/Layout/MainLayout';
import { BackIcon, DownloadIcon } from '../../../components/Dashboard/DataCenterIcons';
import '../../../assets/styles/FDCUploadPage.css';
import '../../../assets/styles/RegularRDCPage.css';
import { commonService } from '../../../services/commonService';
import { ROUTES, headers } from '../../../utils/constants';
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import UserPicker from '../../../components/Common/UserPicker';
import UserMultiPicker from '../../../components/Common/UserMultiPicker';
import { hasPermission } from '../../../utils/permissions';
import '../../../assets/styles/Workflow.css';

// Define proper types
interface FormData {
  fulfillment_type: boolean;
  summary: string;
  crm_id: string;
  criteria: string[];
  geography: string;
  industry: string;
  technologies: string;
  job_title: string;
  company_size: string;
  revenue_size: string;
  number_of_contacts: string;
  data_fields: string;
  other: string;
  project_tire: string;
  brand_name: string;
  record_type: string;
  refulfillment_id: string;
  deal_id: string;
  comment: string;
  files: File[];
  fileInputs: number[];
  assignee_id: number | null;
  participant_ids: number[];
  observer_ids: number[];
}

interface FDCUploadOption {
  projectid: string;
  summary: string;
}

interface UploadStatus {
  status?: 'success' | 'fail';
  message?: string;
  projectid?: string;
  successcount?: number;
  errorcount?: number;
}

type FormField = keyof Omit<FormData, 'criteria' | 'files' | 'fileInputs' | 'assignee_id' | 'participant_ids' | 'observer_ids'>;

const FDCUploadPage: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>({});

  const [fdcOptions, setFdcOptions] = useState<FDCUploadOption[]>([]);
  const [form, setForm] = useState<FormData>({
    fulfillment_type: false,
    summary: '',
    crm_id: '',
    criteria: [],
    geography: '',
    industry: '',
    technologies: '',
    job_title: '',
    company_size: '',
    revenue_size: '',
    number_of_contacts: '',
    data_fields: '',
    other: '',
    project_tire: '',
    brand_name: '',
    record_type: 'fulfillment',
    refulfillment_id: '',
    deal_id: '',
    comment: '',
    files: [],
    fileInputs: [0],
    assignee_id: null,
    participant_ids: [],
    observer_ids: [],
  });

  const [validation, setValidation] = useState({
    submitEnabled: false,
  });

  const uploadFileInChunks = async (
    file: File,
    projectid: string
  ): Promise<boolean> => {
    const CHUNK_SIZE = 1 * 1024 * 1024; // 1 KB
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

    const fileId = uuidv4(); // ✅ STABLE ID

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const start = chunkIndex * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);

      const chunk = file.slice(start, end);

      const formData = new FormData();
      formData.append("projectid", projectid);
      formData.append("file_id", fileId);
      formData.append("file_name", file.name);
      formData.append("chunk_index", String(chunkIndex));
      formData.append("total_chunks", String(totalChunks));
      formData.append("file", chunk);

      try {
        await commonService.uploadChunk(formData);
      } catch {
        return false;
      }
    }

    return true;
  };

  // Handle input changes
  const onChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;


    if (name === 'crm_id') {
      const numericValue = value.replace(/[^0-9]/g, '');
      setForm(prev => ({ ...prev, crm_id: numericValue }));
      validateForm();
      return;
    }

    if (name === 'number_of_contacts') {
      const numericValue = value.replace(/[^0-9]/g, '');
      setForm(prev => ({ ...prev, number_of_contacts: numericValue }));
      validateForm();
      return;
    }

    if (type === 'checkbox' && e.target instanceof HTMLInputElement) {
      const checked = e.target.checked;

      if (name === 'fulfillment_type') {
        setForm(prev => ({
          ...prev,
          fulfillment_type: checked,
          // Clear enrichment fields when checked
          ...(checked ? {
            geography: '',
            industry: '',
            technologies: '',
            job_title: '',
            company_size: '',
            revenue_size: ''
          } : {})
        }));
      } else if (name === 'criteria') {
        const criteriaValue = e.target.value;
        const currentCriteria = [...form.criteria];
        if (checked && !currentCriteria.includes(criteriaValue)) {
          currentCriteria.push(criteriaValue);
        } else if (!checked) {
          const index = currentCriteria.indexOf(criteriaValue);
          if (index > -1) {
            currentCriteria.splice(index, 1);
          }
        }
        setForm(prev => ({ ...prev, criteria: currentCriteria }));
      }
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }

    // Trigger validation after change
    setTimeout(() => validateForm(), 0);
  };

  // Handle file uploads
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const currentFiles = [...form.files];
    const fileList = Array.from(files);

    // Replace or add file at index
    if (index < currentFiles.length) {
      currentFiles[index] = fileList[0];
    } else {
      currentFiles.push(fileList[0]);
    }

    setForm(prev => ({ ...prev, files: currentFiles }));

    // Show add more button if this is the last input
    if (index === form.fileInputs[form.fileInputs.length - 1]) {
      validateForm();
    }
  };

  // Add more file inputs
  const addMoreFileInput = () => {
    const nextIndex = form.fileInputs[form.fileInputs.length - 1] + 1;
    setForm(prev => ({
      ...prev,
      fileInputs: [...prev.fileInputs, nextIndex]
    }));
  };

  // Get FDC IDs for refulfillment
  const getFDCIDs = async () => {
    if (!form.crm_id.trim()) {
      alert('Please enter CRM ID');
      validateForm();
      return;
    }

    try {
      const payload = { crm_id: form.crm_id };
      const response = await commonService.getProjectId(payload);
      console.log('getProjectId response:', response);
      if (response?.message === 'success') {
        setFdcOptions(response?.data || []);
      } else {
        setFdcOptions([]);
      }
    } catch (error) {
      // console.error('Error fetching FDC IDs:', error);
      setFdcOptions([]);
    }
  };

  // Download template
  const downloadTemplate = () => {
    const blob = new Blob([headers.join(",")], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    const now = new Date();

    const pad = (n: number) => String(n).padStart(2, "0");

    const fileName =
      `fdc_${pad(now.getMonth() + 1)}_${pad(now.getDate())}_${now.getFullYear()}_${pad(now.getHours())}_${pad(now.getMinutes())}.csv`;

    link.download = fileName;
    link.click();
  };

  // Validate form
  const validateForm = () => {
    const requiredFields: FormField[] = [
      'summary',
      'crm_id',
      'deal_id',
      'data_fields',
      'project_tire',
      'brand_name',
      'number_of_contacts'
    ];

    // Check required fields
    for (const field of requiredFields) {
      if (!form[field]) {
        setValidation(prev => ({ ...prev, submitEnabled: false }));
        return;
      }
    }

    // Check criteria selection
    if (form.criteria.length === 0) {
      setValidation(prev => ({ ...prev, submitEnabled: false }));
      return;
    }

    // Check files
    if (form.files.length === 0) {
      setValidation(prev => ({ ...prev, submitEnabled: false }));
      return;
    }

    // Check refulfillment ID if selected
    if (form.record_type === 'refulfillment' && !form.refulfillment_id) {
      setValidation(prev => ({ ...prev, submitEnabled: false }));
      return;
    }

    // Check brand name
    if (!form.brand_name) {
      setValidation(prev => ({ ...prev, submitEnabled: false }));
      return;
    }

    // Check assignee (PC Manager)
    if (!form.assignee_id) {
      setValidation(prev => ({ ...prev, submitEnabled: false }));
      return;
    }

    setValidation(prev => ({
      ...prev,
      submitEnabled: true,
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validation.submitEnabled) return;

    setLoading(true);
    setUploadStatus({});

    const formData = new FormData();

    // ✅ Append ONLY valid non-file fields
    Object.entries(form).forEach(([key, value]) => {
      if (key === "files" || key === "fileInputs") return; // 🔥 FIX

      if (key === "criteria") {
        (value as string[]).forEach(item =>
          formData.append("criteria[]", item)
        );
      } else if (key === "participant_ids" || key === "observer_ids") {
        (value as number[]).forEach(id =>
          formData.append(`${key}[]`, String(id))
        );
      } else if (key === "fulfillment_type") {
        formData.append(key, value ? "Enrichment" : "");
      } else if (key === "assignee_id") {
        if (value !== null) formData.append(key, String(value));
      } else {
        formData.append(key, String(value));
      }
    });

    try {
      const response = await commonService.fdcUpload(formData);
      console.log("response:", response);

      const project_id = response?.data?.projectid;
      // console.log(project_id,project_id); return;

      for (const file of form.files) {
        const success = await uploadFileInChunks(file, project_id);
        if (!success) {
          // ❌ Stop everything if ANY file fails
          return;
        }
      }

      // STEP 3: Final merge - no task exists yet at this point, so no task_id
      // to pass; the backend attributes this to the project's own assignee.
      const res = await commonService.finalUpload(project_id);

      setUploadStatus({
        status: "success",
        message: "Upload completed successfully!",
        projectid: response?.data?.projectid,
        successcount: res?.data?.total_uploaded_record_counts || 0,
        errorcount: res?.data?.total_failed_record_counts || 0
      });

      clearForm();


    } catch (err) {
      const isAxiosError = (error: unknown): error is any =>
        typeof error === "object" &&
        error !== null &&
        (error as any).isAxiosError === true;
      let mes = "";
      if (isAxiosError(err)) {
        const errorData = err.response?.data;
        // console.log("errorData", errorData?.message);
        mes = errorData?.message;
      } else {
        mes = "Unexpected error occurred";
      }
      setUploadStatus({
        status: "fail",
        message: mes,
      });
    } finally {
      setLoading(false);
    }
  };


  // Clear form

  const clearForm = () => {
    setForm({
      fulfillment_type: false,
      summary: '',
      crm_id: '',
      criteria: [],
      geography: '',
      industry: '',
      technologies: '',
      job_title: '',
      company_size: '',
      revenue_size: '',
      number_of_contacts: '',
      data_fields: '',
      other: '',
      project_tire: '',
      brand_name: '',
      record_type: 'fulfillment',
      refulfillment_id: '',
      deal_id: '',
      comment: '',
      files: [],
      fileInputs: [0],
      assignee_id: null,
      participant_ids: [],
      observer_ids: [],
    });

    // Clear all file input refs
    fileInputRefs.current.forEach(ref => {
      if (ref) {
        ref.value = '';
      }
    });

    setValidation({ submitEnabled: false });
  };

  // Effect for FDC ID lookup
  useEffect(() => {
    if (form.crm_id && form.record_type === 'refulfillment') {
      const timer = setTimeout(() => {
        getFDCIDs();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [form.crm_id, form.record_type]);

  // Effect for validation
  useEffect(() => {
    validateForm();
  }, [form]);

  useEffect(() => {
    if (!uploadStatus.status) return; // run only when status exists

    const timer = setTimeout(() => {
      setUploadStatus({});
    }, 180000); // 3 min

    return () => clearTimeout(timer); // cleanup on component unmount or re-render
  }, [uploadStatus]);

  if (!hasPermission('fdc_project:create')) {
    return (
      <MainLayout>
        <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
          You don't have permission to create FDC projects.
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="fdc-upload-page">
        {/* Header */}
        <div className="fdc-upload-header">
          <div className="header-left">
            <button
              onClick={() => navigate(-1)}
              className="regular-rdc-back-button"
            >
              <BackIcon />
              Back
            </button>
            <span className="fdc-upload-title">FDC Upload</span>
          </div>
          <div className="header-right">
            <a
              className="top_link"
              href={ROUTES.FDC_LIST}
              target="_blank"
              rel="noopener noreferrer"
            >
              View Project Logs
            </a>
            <span
              className="top_link"
              onClick={downloadTemplate}
              style={{ cursor: 'pointer' }}
            >
              <DownloadIcon />
              <span className='ml-2'>Download Template</span>

            </span>
          </div>
        </div>

        <div className="line"></div>

        {/* Form */}
        <form className="fdc-upload-form" onSubmit={handleSubmit}>
          <div className="form-container">
            {/* Fulfillment Type */}
            <div className="form-row">
              <div className="form-label">Fulfillment Type</div>
              <div className="form-inputs" style={{ flex: "none" }}>
                <label className="checkbox-label mt-2">
                  <input
                    type="checkbox"
                    name="fulfillment_type"
                    checked={form.fulfillment_type}
                    onChange={onChange}
                  />
                  <span>Enrichment</span>
                </label>
              </div>
            </div>

            {/* Task Title */}
            <div className="form-row">
              <div className="form-label">Task Title</div>
              <div className="form-inputs">
                <input
                  type="text"
                  name="summary"
                  value={form.summary}
                  onChange={onChange}
                  placeholder="Enter project Summary"
                />
              </div>
            </div>

            {/* CRM ID */}
            <div className="form-row">
              <div className="form-label">CRM ID</div>
              <div className="form-inputs">
                <input
                  type="text"
                  name="crm_id"
                  value={form.crm_id}
                  onChange={onChange}
                  onBlur={getFDCIDs}
                  placeholder="Enter CRM ID"
                />
              </div>
            </div>
            {/* CRM ID */}
            <div className="form-row">
              <div className="form-label">Deal ID</div>
              <div className="form-inputs">
                <input
                  type="text"
                  name="deal_id"
                  value={form.deal_id}
                  onChange={onChange}
                  placeholder="Enter Deal ID"
                />
              </div>
            </div>

            {/* Assignee (PC Manager) */}
            <div className="form-row">
              <div className="form-label">Assignee (PC Manager)</div>
              <div className="form-inputs">
                <UserPicker
                  role="pc_manager"
                  value={form.assignee_id}
                  onChange={(id) => setForm(prev => ({ ...prev, assignee_id: id }))}
                  placeholder="Type a PC Manager's name..."
                />
              </div>
            </div>

            {/* Participants */}
            <div className="form-row">
              <div className="form-label">Participants</div>
              <div className="form-inputs">
                <UserMultiPicker
                  value={form.participant_ids}
                  onChange={(ids) => setForm(prev => ({ ...prev, participant_ids: ids }))}
                  placeholder="Add participants..."
                />
              </div>
            </div>

            {/* Observers */}
            <div className="form-row">
              <div className="form-label">Observers</div>
              <div className="form-inputs">
                <UserMultiPicker
                  value={form.observer_ids}
                  onChange={(ids) => setForm(prev => ({ ...prev, observer_ids: ids }))}
                  placeholder="Add observers..."
                />
              </div>
            </div>

            {/* List Type */}
            <div className="form-row">
              <div className="form-label">List Type</div>
              <div className="form-inputs" style={{ flex: "none" }}>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="criteria"
                    value="B2B"
                    checked={form.criteria.includes('B2B')}
                    onChange={onChange}
                  />
                  <span>B2B</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="criteria"
                    value="B2C"
                    checked={form.criteria.includes('B2C')}
                    onChange={onChange}
                  />
                  <span>B2C</span>
                </label>
              </div>
            </div>

            {/* Geography */}
            <div className="form-row">
              <div className="form-label">Geography</div>
              <div className="form-inputs">
                <input
                  type="text"
                  name="geography"
                  value={form.geography}
                  onChange={onChange}
                  placeholder="Enter Geography"
                  disabled={form.fulfillment_type}
                />
              </div>
            </div>

            {/* Industry */}
            <div className="form-row">
              <div className="form-label">Industry</div>
              <div className="form-inputs">
                <input
                  type="text"
                  name="industry"
                  value={form.industry}
                  onChange={onChange}
                  placeholder="Enter Industry"
                  disabled={form.fulfillment_type}
                />
              </div>
            </div>

            {/* Technologies */}
            <div className="form-row">
              <div className="form-label">Technologies</div>
              <div className="form-inputs">
                <input
                  type="text"
                  name="technologies"
                  value={form.technologies}
                  onChange={onChange}
                  placeholder="Enter Technologies"
                  disabled={form.fulfillment_type}
                />
              </div>
            </div>

            {/* Job Titles */}
            <div className="form-row">
              <div className="form-label">Job Titles</div>
              <div className="form-inputs">
                <input
                  type="text"
                  name="job_title"
                  value={form.job_title}
                  onChange={onChange}
                  placeholder="Enter Job Title"
                  disabled={form.fulfillment_type}
                />
              </div>
            </div>

            {/* Company Size */}
            <div className="form-row">
              <div className="form-label">Company Size</div>
              <div className="form-inputs">
                <input
                  type="text"
                  name="company_size"
                  value={form.company_size}
                  onChange={onChange}
                  placeholder="Enter Company Size"
                  disabled={form.fulfillment_type}
                />
              </div>
            </div>

            {/* Company Revenue */}
            <div className="form-row">
              <div className="form-label">Company Revenue</div>
              <div className="form-inputs">
                <input
                  type="text"
                  name="revenue_size"
                  value={form.revenue_size}
                  onChange={onChange}
                  placeholder="Enter Company Revenue"
                  disabled={form.fulfillment_type}
                />
              </div>
            </div>

            {/* Number of Contacts */}
            <div className="form-row">
              <div className="form-label">Number of Contacts</div>
              <div className="form-inputs">
                <input
                  type="text"
                  name="number_of_contacts"
                  value={form.number_of_contacts}
                  onChange={onChange}
                  placeholder="Enter Number of Contacts"
                />
              </div>
            </div>

            {/* Data Fields */}
            <div className="form-row">
              <div className="form-label">Data Fields</div>
              <div className="form-inputs">
                <textarea
                  name="data_fields"
                  value={form.data_fields}
                  onChange={onChange}
                  placeholder="Enter Data Fields"
                  rows={3}
                />
              </div>
            </div>

            {/* Other Criteria */}
            <div className="form-row">
              <div className="form-label">Other Criteria</div>
              <div className="form-inputs" style={{ minWidth: 0 }}>
                {/* <textarea
                  name="other"
                  value={form.other}
                  onChange={onChange}
                  placeholder="Enter other criteria"
                  rows={3}
                /> */}
                <CKEditor
                  editor={ClassicEditor as any}
                  data={form.other || ""}
                  onChange={(_, editor) => {
                    const data = editor.getData();

                    setForm((prev: any) => ({
                      ...prev,
                      other: data
                    }));
                  }}
                  config={{
                    placeholder: "Enter other criteria"
                  }}
                />
              </div>
            </div>

            {/* Project Tier */}
            <div className="form-row">
              <div className="form-label">Project Tier</div>
              <div className="form-inputs">
                <input
                  type="text"
                  name="project_tire"
                  value={form.project_tire}
                  onChange={onChange}
                  placeholder="Enter project Tier"
                />
              </div>
            </div>

            {/* Brand Name */}
            <div className="form-row">
              <div className="form-label">Brand Name (DC,VL,CA,ETC)</div>
              <div className="form-inputs">
                <select
                  name="brand_name"
                  value={form.brand_name}
                  onChange={onChange}
                >
                  <option value="">-- Select brand name --</option>
                  <option value="DataCaptive">DataCaptive</option>
                  <option value="ContactAffix">ContactAffix</option>
                  <option value="ProDataLabs">ProDataLabs</option>
                  <option value="VisitorsList">VisitorsList</option>
                  <option value="TechDataPark">TechDataPark</option>
                  <option value="CampaignLake">CampaignLake</option>
                  <option value="ESalesClub">ESalesClub</option>
                  <option value="InboxCEO">InboxCEO</option>
                  <option value="MedicoLeads">MedicoLeads</option>
                  <option value="ExpoCaptive">ExpoCaptive</option>
                </select>
              </div>
            </div>

            {/* Project Type */}
            <div className="form-row">
              <div className="form-label">Project Type</div>
              <div className="form-inputs">
                <select
                  name="record_type"
                  value={form.record_type}
                  onChange={onChange}
                >
                  <option value="fulfillment">Fulfillment</option>
                  <option value="refulfillment">Refulfillment</option>
                </select>
              </div>
            </div>

            {/* FDC Upload ID (shown only for refulfillment) */}
            {form.record_type === 'refulfillment' && (
              <div className="form-row">
                <div className="form-label">FDC Upload ID</div>
                <div className="form-inputs">
                  <select
                    name="refulfillment_id"
                    value={form.refulfillment_id}
                    onChange={onChange}
                  >
                    <option value="">-- Select FDC Upload ID --</option>
                    {fdcOptions.map(option => (
                      <option key={option.projectid} value={option.projectid}>
                        {option.projectid} ({option.summary})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
            {form.record_type === 'refulfillment' && (
              <div className="form-row">
                <div className="form-label">Comment</div>
                <div className="form-inputs">
                  <textarea
                    name="comment"
                    value={form.comment}
                    onChange={onChange}
                    placeholder="Enter Comment"
                    rows={3}
                  />
                </div>
              </div>
            )}
            {/* File Upload */}
            <div className="form-row">
              <div className="form-label">Choose File</div>
              <div className="form-inputs">
                {form.fileInputs.map((index) => (
                  <div key={index} className="file-input-container">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={(e) => handleFileChange(e, index)}
                      ref={el => { fileInputRefs.current[index] = el; }}
                      className="file-input"
                    />
                  </div>
                ))}
                {form.files.length > 0 && form.files[form.files.length - 1] && (
                  <div className="addmore" onClick={addMoreFileInput}>
                    Add more
                  </div>
                )}
              </div>
            </div>

            <div className="line"></div>

            {/* Form Actions */}
            <div className="form-actions">
              <button
                type="submit"
                className={`submit-button ${validation.submitEnabled ? 'button-enabled' : 'button-disabled'}`}
                disabled={!validation.submitEnabled || loading}
              >
                {loading ? 'Uploading...' : 'Submit Project'}
              </button>
              <div className="discard mt-2" onClick={clearForm}>
                Discard
              </div>
            </div>
            {uploadStatus.status && (
              <div className="upload-status" style={{ display: "block" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <div className="status-message">
                    {uploadStatus.status === 'success' ? (
                      <>
                        <span className="success-icon">✓</span> Successfully uploaded
                      </>
                    ) : (
                      <>
                        <span className="error-icon" style={{ fontSize: "14px" }}>{uploadStatus.message || "Error occurred in uploading"}</span>
                      </>
                    )}
                  </div>

                  {/* TRACKING ID */}
                  {uploadStatus.projectid && (
                    <div className="tracking-id mt-2">
                      Tracking ID: <span className="tracking-number">{uploadStatus.projectid}</span>
                    </div>
                  )}
                </div>
                {/* SUCCESS/ERROR COUNT */}
                {uploadStatus.successcount !== undefined && (
                  <div className="success-error-count mt-2" style={{ textAlign: "left", fontSize: "12px" }}>
                    Success Count: <span style={{ color: "Green" }}>{uploadStatus.successcount}</span> | Error Count: <span style={{ color: "red" }}>{uploadStatus.errorcount}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

export default FDCUploadPage;